import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, forkJoin, map, Observable, mergeMap, of, switchMap, withLatestFrom, takeUntil, Subject } from 'rxjs';
import { selectInfo, selectStateCode, selectUseTestTrustDigitalToPdfFlow } from '../../account-info-header/reducers';
import { State } from '../../account-info-header/reducers/account-info.reducers';
import { RemoveHyphenFromAccountPipe } from '../../shared/pipes/remove-hyphen-from-account.pipe';
import {
  Constants,
  PageTypes,
  frequencyDict,
  pdfLabelsSubmitDetails,
  pdfLabelsToDetails,
  positionConstants,
  transactionTypes,
  transactionTypesDict,
  transferTypes,
  pageInContext,
  RegTypeCode,
  yesNo,
  checkRecipientTypeDict
} from '../../shared/utilities/Constants';
import { getAllLoaFieldDetails } from '../../shared/utilities/pdf';
import { ReviewPageActions } from '../actions';
import {
  CheckForm,
  CheckTransactionDetails,
  isCheckTransactionForm,
  isInternalTransferForm,
  isWireForm,
  ItForm,
  TransactionForm,
  WireForm,
  WireTransactionDetails,
} from '../models/form/TransactionForm';
import { InternalTransferInfo } from '../models/internal-transfer/InternalTransferInfo';
import { Detail, toDetailMap } from '../models/pdf/Detail';
import { LetterOfAuthorizationRequest } from '../models/pdf/LetterOfAuthorizationRequest';
import { LetterOfAuthorizationResponse } from '../models/pdf/LetterOfAuthorizationResponse';
import { WireInfo } from '../models/wire/WireInfo';
import {
  CashieringCheckInputDetails,
  CheckInfo,
} from '../models/check/CheckInfo';
import {
  selectCheckInfo,
  selectCostBasisMethod,
  selectHasSubmitAndCreateError,
  selectInternalTransferInfo,
  selectInternalTransfersInfo,
  selectStagedCostBasisMethod,
  selectStagedTransactionForm,
  selectSubmitAndCreateToggledOn,
  selectUseStandingAuth,
  selectWireInfo,
} from '../reducers';
import { LetterOfAuthorizationService } from '../services/letter-of-authorization.service';
import { CodeAndDescription } from '../models/shared/CodeAndDescription';
import { TableColumn } from '../../shared/types/TableColumn';
import { FormatDatePipe } from '../../shared/pipes/FormatDatePipe';
import { StandingAuthReviewPageActions } from '../../standing-authorizations/actions';
import { selectStagedStandingAuthForm, selectStandingAuthCheckInfo, selectStandingAuthInternalTransferInfo, selectStandingAuthWireInfo } from '../../standing-authorizations/reducers';
import { selectIsEnableWiresCondensedLayout } from '../../feature-flags/reducers';
import { CheckStandingAuthDetails, StandingAuthorizationForm, WireStandingAuthDetails, isCheckStandingAuthFormSA, isInternalTransferFormSA, isWireFormSA } from '../../standing-authorizations/models/standingAuthorizationForm';
import { StandingAuthCheckInfo } from '../../standing-authorizations/models/StandingAuthCheckInfo';
import { InternalTransferInfoSA } from '../../standing-authorizations/models/InternalTransferInfoSA';
import { selectEAuthInfo, selectViewAccountDetails } from '../../shared/reducers';
import { EAuthInfo } from '../../shared/models/EAuthInfo';
import { getProcessDateInIsoFormat } from '../components/transactions-table/table-helpers';
import { ViewDetails } from '../../transaction-view/models/ViewDetails';
import { selectViewDetails } from '../../transaction-view/reducers';
import { SubmitAndCreateStatus } from '../models/shared/SubmitAndCreateStatus';
import { ViewPageActions } from '../../transaction-view/actions';
import { FormatIsoAddressFieldsService } from '../../shared/services/format-isoAddressFields.service';
import { InternalTransfersInfo } from '../models/internal-transfer/InternalTransfersInfo';
import { CountryDescriptionFromCode } from '../../shared/utilities/CountryDescriptionFromCode';
import { MONTH_LIST } from '../models/wire/MonthList';
import { OptionDropdownSelect } from '../models/OptionDropdownSelect';
import { ViewAccountDetails } from '../models/view-page/ViewPage';
import { TitleCasePipe } from '@angular/common';
import { FormatPercentPipe } from '../../shared/pipes/FormatPercentPipe';
import { AccountInfo } from '../../account-info-header/models';
import { MaskAccountNumberPipe } from '../../shared/pipes/mask-account-number.pipe';
import { selectIsCreateSloaToggleFlagEnabled } from '../../feature-flags/reducers';
import { selectHasAccessInitiateMoneyMovement } from '../../authorize/reducers';

@Injectable()
export class LetterOfAuthorizationEffects {
  hyphenPipe = new RemoveHyphenFromAccountPipe();
  formatDatePipe = new FormatDatePipe();
  useSloa$: Observable<boolean>;
  useStandingAuth: boolean;
  isEnableWiresCondensedLayout = false;
  destroy$: Subject<boolean> = new Subject();
  countryListNameValue: { value:string, name:string }[];
  constructor(
    private actions$: Actions,
    private accountInfoService: LetterOfAuthorizationService,
    private store: Store,
    private formatIsoAddressFieldsService: FormatIsoAddressFieldsService,
    private titleCase: TitleCasePipe,
    private percentFormatPipe: FormatPercentPipe,
    private maskAccountNumberPipe: MaskAccountNumberPipe
  ) {
    this.store
      .select(selectIsEnableWiresCondensedLayout)
      .pipe(takeUntil(this.destroy$))
      .subscribe(enabled => (this.isEnableWiresCondensedLayout = enabled));
  }

  letterOfAuthorization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewPageActions.generateAuthorizationForm),
      withLatestFrom(
        this.store.select(selectStagedTransactionForm),
        this.store.select(selectViewAccountDetails),
        this.store.select(selectInfo),
        this.store.select(selectWireInfo),
        this.store.select(selectInternalTransferInfo),
        this.store.select(selectInternalTransfersInfo),
        this.store.select(selectCheckInfo),
        this.store.select(selectViewDetails),
        this.store.select(selectEAuthInfo),
        this.store.select(selectHasSubmitAndCreateError),
        this.store.select(selectStateCode),
        this.store.select(selectCostBasisMethod),
        this.store.select(selectStagedCostBasisMethod),
        this.store.select(selectUseTestTrustDigitalToPdfFlow),
        this.store.select(selectSubmitAndCreateToggledOn),
        this.store.select(selectStandingAuthWireInfo),
        this.store.select(selectIsCreateSloaToggleFlagEnabled),
        this.store.select(selectHasAccessInitiateMoneyMovement)
      ),
      switchMap(
        ([
          action,
          form,
          viewAccountDetails,
          accountInfo,
          wireInfo,
          internaltransferInfo,
          internaltransfersInfo,
          checkInfo,
          viewDetails,
          eauthInfo,
          submitAndCreateStatus,
          stateCode,
          defaultCostBasisMethod,
          selectedCostBasisMethod,
          useTestTrustDigitalToPdfFlow,
          submitAndCreateToggledOn,
          standingAuthWireInfo,
          isCreateSloaToggleFlagEnabled,
          hasAccessInitiateMoneyMovement
        ]) => {
          const shouldUseCombinedProcessIdForSplitCustomRules =
            !!action.includeCreateSloaDetails
            && isCreateSloaToggleFlagEnabled
            && hasAccessInitiateMoneyMovement
            && submitAndCreateToggledOn
            && !!wireInfo?.authResponse?.canRelease
            && !wireInfo?.authResponse?.pendForRelease
            && !!standingAuthWireInfo?.authResponse?.canRelease
            && !standingAuthWireInfo?.authResponse?.pendForRelease;

          // When both transactions can release, keep the existing single-LOA
          // behavior by using one request with both case identifiers.
          const wireInfoForLoa = shouldUseCombinedProcessIdForSplitCustomRules
            ? {
                ...wireInfo,
                caseKey: this.mergeSplitWireCaseValue(wireInfo?.caseKey, standingAuthWireInfo?.caseKey),
              }
            : wireInfo;

          let request = this.generateLOAFormRequest(
            form,
            viewAccountDetails,
            accountInfo,
            wireInfoForLoa,
            internaltransferInfo,
            internaltransfersInfo,
            checkInfo,
            action.source,
            viewDetails,
            eauthInfo,
            submitAndCreateStatus,
            stateCode,
            defaultCostBasisMethod,
            selectedCostBasisMethod,
            useTestTrustDigitalToPdfFlow,
            submitAndCreateToggledOn,
            action.includeCreateSloaDetails
          );
          return this.accountInfoService
            .callLetterOfAuthorization(request)
            .pipe(
              map((response: LetterOfAuthorizationResponse) => {
                return ReviewPageActions.generateAuthorizationFormSuccessful({
                  response,
                });
              }),
              catchError((err) =>
                of(
                  ReviewPageActions.generateAuthorizationFormFail({
                    errorMsg: err.message,
                  })
                )
              )
            );
        }
      )
    )
  );

  letterOfAuthorizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewPageActions.generateMultipleAuthorizationForms),
      withLatestFrom(
        this.store.select(selectStagedTransactionForm),
        this.store.select(selectViewAccountDetails),
        this.store.select(selectInfo),
        this.store.select(selectWireInfo),
        this.store.select(selectInternalTransfersInfo),
        this.store.select(selectInternalTransferInfo),
        this.store.select(selectCheckInfo),
        this.store.select(selectViewDetails),
        this.store.select(selectEAuthInfo),
        this.store.select(selectHasSubmitAndCreateError),
        this.store.select(selectStateCode),
        this.store.select(selectCostBasisMethod),
        this.store.select(selectStagedCostBasisMethod),
        this.store.select(selectUseTestTrustDigitalToPdfFlow)
      ),
      mergeMap(
        ([
          action,
          form,
          viewAccountDetails,
          accountInfo,
          wireInfo,
          internalTransfersInfo,
          internalTransferInfo,
          checkInfo,
          viewDetails,
          eauthInfo,
          submitAndCreateStatus,
          stateCode,
          defaultCostBasisMethod,
          selectedCostBasisMethod,
          useTestTrustDigitalToPdfFlow
        ]) => {
          return forkJoin(action.toAccounts.map(toAccount => {
              let request = this.generateLOAFormRequest(
                form,
                viewAccountDetails,
                accountInfo,
                wireInfo,
                this.getInternalTransferInfo(internalTransferInfo, internalTransfersInfo, toAccount),
                internalTransfersInfo,
                checkInfo,
                action.source,
                viewDetails,
                eauthInfo,
                submitAndCreateStatus,
                stateCode,
                defaultCostBasisMethod,
                selectedCostBasisMethod,
                useTestTrustDigitalToPdfFlow
              );
            return this.accountInfoService
              .callLetterOfAuthorization(request)
              .pipe(
                map((response: LetterOfAuthorizationResponse) =>
                  response
                ),
              );
          })).pipe(
            map((responses) => {
              return ReviewPageActions.generateAuthorizationFormsSuccessful({ responses: responses })
            }
            ),
            catchError(error =>
              of(ReviewPageActions.generateAuthorizationFormsFail({ errorMsg: error }))
            )
          )
        }
      )
    )
  );

  letterOfAuthorizationCreateStandingAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthReviewPageActions.generateAuthorizationForm),
      withLatestFrom(
        this.store.select(selectStagedStandingAuthForm),
        this.store.select(selectStagedTransactionForm),
        this.store.select(selectInfo),
        this.store.select(selectStandingAuthCheckInfo),
        this.store.select(selectStandingAuthInternalTransferInfo),
        this.store.select(selectStandingAuthWireInfo),
        this.store.select(selectUseTestTrustDigitalToPdfFlow),
        this.store.select(selectStateCode),
        this.store.select(selectIsCreateSloaToggleFlagEnabled)
      ),
      switchMap(
        ([_action, form, transactionForm, accountInfo, checkInfo, internalTransferInfo, wireInfo, useTestTrustDigitalToPdfFlow, stateCode, isCreateSloaToggleFlagEnabled]) => {
          const standingAuthForm = this.resolveStandingAuthFormForLoa(form, transactionForm, isCreateSloaToggleFlagEnabled);
          let request = this.generateLOAFormRequestCreateStandingAuth(
            standingAuthForm,
            accountInfo,
            checkInfo,
            internalTransferInfo,
            wireInfo,
            useTestTrustDigitalToPdfFlow,
            stateCode
          );
          return this.accountInfoService
            .callLetterOfAuthorization(request)
            .pipe(
              map((response: LetterOfAuthorizationResponse) => {
                return StandingAuthReviewPageActions.generateAuthorizationFormSuccessful({
                  response,
                });
              }),
              catchError((err) =>
                of(
                  StandingAuthReviewPageActions.generateAuthorizationFormFail({
                    errorMsg: err.message,
                  })
                )
              )
            );
        }
      )
    )
  );

  letterofAuthorizationViewPageStandingAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ViewPageActions.generateAuthorizationFormStandingAuth),
      withLatestFrom(
        this.store.select(selectViewDetails),
        this.store.select(selectInfo),
        this.store.select(selectWireInfo),
        this.store.select(selectInternalTransferInfo),
        this.store.select(selectCheckInfo),
        this.store.select(selectStagedTransactionForm),
        this.store.select(selectStateCode)
      ),
      switchMap(
        ([_action, viewDetails, accountInfo, wireInfo, itInfo, checkInfo, form, stateCode]) => {
          let request = this.generateStandingAuthLOAFormRequestFromViewPage(
            form,
            accountInfo,
            wireInfo,
            itInfo,
            checkInfo,
            viewDetails,
            stateCode
          );
          return this.accountInfoService
            .callLetterOfAuthorization(request)
            .pipe(
              map((response: LetterOfAuthorizationResponse) => {
                return ViewPageActions.generateAuthorizationFormStandingAuthSuccessful({response});
              }),
              catchError((err) =>
                of(ViewPageActions.generateAuthorizationFormStandingAuthFail({errorMsg: err.message}))
              )
            );
        }
      )
    )
  );

  // When making the split custom-rules I&R standing-auth LOA call, the data we need
  // can still live on the normal transaction form, so use this helper to read from
  // the right source for that request only when the flagged flow is enabled.
  resolveStandingAuthFormForLoa(
    form: StandingAuthorizationForm,
    transactionForm: TransactionForm,
    isCreateSloaToggleFlagEnabled: boolean
  ): StandingAuthorizationForm {
    if (!isCreateSloaToggleFlagEnabled) {
      return form;
    }

    if (
      isCreateSloaToggleFlagEnabled
      && transactionForm?.transactionType === transactionTypes.wire
      && isWireForm(transactionForm)
    ) {
      const wireDetails = transactionForm.transactionDetails as WireTransactionDetails;

      return {
        ...form,
        transactionType: transactionTypes.wire,
        standingAuthorizationDetails: {
          ...wireDetails,
          authorizationNickname: '',
          receivingBankName1: wireDetails.receivingBankName1 || wireDetails.receivingBankName,
          fedTaxPercent: wireDetails.taxWithholdingsFormGroup?.fedTaxPercent ?? '',
          stateTaxPercent: wireDetails.taxWithholdingsFormGroup?.stateTaxPercent ?? '',
        } as unknown as WireStandingAuthDetails,
      };
    }

    return form;
  }

  private mergeSplitWireCaseValue(primaryValue?: string, secondaryValue?: string): string {
    const uniqueValues = [...new Set(
      [primaryValue, secondaryValue]
        .filter(Boolean)
        .flatMap(value => value!.split(',').map(item => item.trim()).filter(Boolean))
    )];

    return uniqueValues.join(', ');
  }

  generateLOAFormRequest(
    form: TransactionForm,
    viewAccountDetails: ViewAccountDetails | undefined,
    accountInfo: State,
    wireInfo: WireInfo,
    itInfo: InternalTransferInfo,
    itsInfo: InternalTransfersInfo,
    checkInfo: CheckInfo,
    source: PageTypes,
    viewDetails: ViewDetails,
    eauthInfo?: EAuthInfo,
    submitAndCreateStatus?: SubmitAndCreateStatus,
    stateCode?: string,
    defaultCostBasisMethod?: string,
    selectedCostBasisMethod?: string,
    useTestTrustDigitalToPdfFlow?: boolean,
    submitAndCreateToggledOn?: boolean,
    includeCreateSloaDetails?: boolean
  ): LetterOfAuthorizationRequest {
    let id = source === pageInContext.view ? 'transaction-view-page' : 'transaction-review-page';
    let transactionType = form.transactionType;
    let isIra = (source === 'view')? viewAccountDetails?.isIra: accountInfo.classifications.isIra;
    let isSubmitAndCreate = false;

    let stateTaxLabel = pdfLabelsSubmitDetails.stateTax;
    if (!accountInfo.classifications.isNraAccount && stateCode) {
      stateTaxLabel = `State Tax (${stateCode}):`
    };

    let { toDetails, submitDetails, createSloaDetails } = getAllLoaFieldDetails(id, 'loa');

    switch (transactionType) {
      case transactionTypes.check:
        isSubmitAndCreate = checkInfo.isSubmitAndCreate;
        break;
      case transactionTypes.internalTransfer:
        isSubmitAndCreate = itInfo.isSubmitAndCreate;
        break;
      case transactionTypes.wire:
        isSubmitAndCreate = submitAndCreateToggledOn ? true : wireInfo.isSubmitAndCreate;
        break;
      default:
        isSubmitAndCreate = false
        break;
    }
    // If custom-rules submit-and-create has different can-release outcomes on the two
    // transactions, we want separate LOA PDFs, so this controls removing create-SLOA
    // details from the main LOA call in that case.
    const shouldIncludeCreateSloaDetails = includeCreateSloaDetails ?? isSubmitAndCreate;

    const loaRequestPayload: Partial<LetterOfAuthorizationRequest> = {
      subAccountId: (source === 'view' && transactionType!=transactionTypes.wire) ? viewAccountDetails?.subAccountId.replace("-",''): accountInfo.accountId,
      masterAccountId:  accountInfo.masterAccount.padStart(8,'0'),
      transactionType: form.transactionType,
      toDetails: toDetails,
      submitDetails: submitDetails,
      messageDetails: [],
    }
    // adding the taxRemittance fields to submit details if not exists
    const index = loaRequestPayload?.submitDetails?.findIndex(obj => obj.name === pdfLabelsSubmitDetails.taxId);
    let wireTransactionDetails = form.transactionDetails as WireTransactionDetails; 
    if (index === -1 && wireInfo?.isIrsAba) {
      loaRequestPayload.submitDetails = [
      ...(loaRequestPayload.submitDetails ?? []),
      new Detail(pdfLabelsSubmitDetails.transmissionNotesIrs, [wireInfo?.taxRemittance?.additionalInfo]),
      new Detail(pdfLabelsSubmitDetails.taxId, [this.maskAccountNumberPipe.transform(wireTransactionDetails?.taxPayerId)]),
      new Detail(pdfLabelsSubmitDetails.taxTypeCode, [wireTransactionDetails?.taxTypeCode]),
      new Detail(pdfLabelsSubmitDetails.taxYear, [wireTransactionDetails?.taxYear]),
      new Detail(pdfLabelsSubmitDetails.taxMonth, [this.formatIrsTaxMonth(wireTransactionDetails?.taxMonth)]),
      ];
    }

    // condensed layout scrapes `additionalWireInstructions` for the transmission-notes row,
    // so for IRS wires we drop it (and vice versa) to keep the two labels mutually exclusive on the PDF.
    if (transactionType === transactionTypes.wire && this.isEnableWiresCondensedLayout) {
      const excludedName = wireInfo?.isIrsAba
        ? pdfLabelsSubmitDetails.additionalWireInstructions
        : pdfLabelsSubmitDetails.transmissionNotesIrs;
      loaRequestPayload.submitDetails = (loaRequestPayload.submitDetails ?? []).filter(
        obj => obj.name !== excludedName,
      );
    }

    // shifting the Tax remittance objects in last to appear in loa pdf's if irs aba
    const submitTaxObjectsToMove = loaRequestPayload?.submitDetails?.filter(obj => (obj.name == pdfLabelsSubmitDetails.transmissionNotesIrs) || (obj.name == pdfLabelsSubmitDetails.taxId) || (obj.name == pdfLabelsSubmitDetails.taxTypeCode)
                                    || (obj.name == pdfLabelsSubmitDetails.taxYear) || (obj.name == pdfLabelsSubmitDetails.taxMonth));

    const remainingsubmitObject = loaRequestPayload?.submitDetails?.filter(obj => (obj.name != pdfLabelsSubmitDetails.transmissionNotesIrs) && (obj.name != pdfLabelsSubmitDetails.taxId) && (obj.name != pdfLabelsSubmitDetails.taxTypeCode)
                                    && (obj.name != pdfLabelsSubmitDetails.taxYear) && (obj.name != pdfLabelsSubmitDetails.taxMonth));
    loaRequestPayload.submitDetails = [];
    loaRequestPayload.submitDetails = [...remainingsubmitObject ?? [], ...submitTaxObjectsToMove ?? []];

    // For wire transactions, enforce the canonical LOA PDF field order regardless of DOM scrape
    // order so the new condensed layout matches the original layout.
    if (transactionType === transactionTypes.wire) {
      const desiredWireOrder = [
        pdfLabelsSubmitDetails.transactionType,
        pdfLabelsSubmitDetails.processDate,
        pdfLabelsSubmitDetails.wireAmount,
        pdfLabelsSubmitDetails.wireFee,
        pdfLabelsSubmitDetails.fedTax,
        pdfLabelsSubmitDetails.stateTax,
        pdfLabelsSubmitDetails.withdrawalAmount,
        pdfLabelsSubmitDetails.distributionReason,
        pdfLabelsSubmitDetails.additionalWireInstructions,
        pdfLabelsSubmitDetails.transmissionNotesIrs,
      ];
      const orderIndex = new Map(desiredWireOrder.map((name, idx) => [name, idx]));
      const knownByName = new Map<string, Detail>();
      const others: Detail[] = [];
      for (const detail of loaRequestPayload.submitDetails ?? []) {
        if (orderIndex.has(detail.name)) {
          knownByName.set(detail.name, detail);
        } else {
          others.push(detail);
        }
      }
      const orderedKnown = desiredWireOrder
        .map(name => knownByName.get(name))
        .filter((d): d is Detail => !!d);
      loaRequestPayload.submitDetails = [...orderedKnown, ...others];
    }

    if (shouldIncludeCreateSloaDetails) {
      loaRequestPayload.createSloaDetails = createSloaDetails;
    }

    let loaRequest = new LetterOfAuthorizationRequest(loaRequestPayload);

    if (stateCode) {
      loaRequest.state = stateCode;
    }

    if (transactionType === 'wire' && isWireForm(form)) {
      let wireTransactionDetails = form.transactionDetails;
      loaRequest.submitDetails = [
        ...loaRequest.submitDetails,
        ...(isIra
          ? [
            new Detail(pdfLabelsSubmitDetails.withholdFederalTax, [
              wireInfo.withholdFedTax ? 'Yes' : 'No',
            ]),
            new Detail(pdfLabelsSubmitDetails.withholdStateTax, [
              wireInfo.withholdStateTax ? 'Yes' : 'No',
            ]),
          ]
          : []),
        new Detail(pdfLabelsSubmitDetails.requestedDate, [
          source === pageInContext.review
            ? wireInfo.requestedDate
            : viewDetails.auditFields.request.date,
        ]),
        ...(source === pageInContext.view && viewDetails.auditFields.isUpdated
          ? [
              new Detail(pdfLabelsSubmitDetails.lastModifiedDate, [
                viewDetails.auditFields.update.date
              ]),
            ]
          : []),
      ];

      if (wireInfo.processDateFields.isExtendedProcessingWindow
        && wireInfo.processDateFields.useExtendedProcessDates
        && eauthInfo?.isOptedOut
      ) {
        var processDateDetail = loaRequest.submitDetails.find(detail => detail.name === 'Process Date:');
        if (processDateDetail) {
          var extendedDate = new Date(getProcessDateInIsoFormat(wireInfo.processDateFields.extendedProcessDates[0]));
          var selectedDate = new Date(getProcessDateInIsoFormat(processDateDetail.values[0]));
          if (extendedDate.toDateString() === selectedDate.toDateString()) {
            processDateDetail.values[0] = wireInfo.processDateFields.standardProcessDates[0];
          }
        }
      }

      if (shouldIncludeCreateSloaDetails) {
        loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.transactionType, [transactionTypesDict[transactionTypes.wire]]));
        loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.processDate, [form.transactionDetails.selectedDate]));
        // The condensed layout IRS wires emit `transmissionNotesIrs` below instead,
        // so skip `additionalWireInstructions` here to avoid a duplicate row on the SLOA PDF.
        if (!this.isEnableWiresCondensedLayout || !wireInfo?.isIrsAba) {
          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.additionalWireInstructions, [form.transactionDetails.transmissionNotes]));
        }
        loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.wireAmount, [wireInfo.wireAmount]));
        if (wireInfo?.isIrsAba) {
          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.transmissionNotesIrs, [wireInfo?.taxRemittance?.additionalInfo]));
          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.taxId, [this.maskAccountNumberPipe.transform(wireInfo.taxRemittance?.taxId)]));
          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.taxTypeCode, [wireInfo?.taxRemittance?.taxTypeCode]));
        }
        loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.requestedDate, source === pageInContext.view ? [form.transactionDetails.requestedDate] : [wireInfo.requestedDate]));
        if (isIra) {
          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.withholdFederalTax, [
            wireInfo.withholdFedTax ? 'Yes' : 'No',
          ]));
          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.fedTax, [wireInfo.fedTaxPercent]));
          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.withholdStateTax, [wireInfo.withholdStateTax ? 'Yes' : 'No']));
          loaRequest.createSloaDetails.push(new Detail(stateTaxLabel, [wireInfo.stateTaxPercent]));
          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.distributionReason, [wireInfo.distributionReasonDescription]));
        }
      }

      this.store.select(selectUseStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(useStandingAuth => this.useStandingAuth = useStandingAuth);

      this.countryListNameValue = wireInfo.countryList.map((country) => {
        return { value: country.code, name: country.description };
      })

      loaRequest.toDetails = this.getIsoToDetailsLoaRequest(loaRequest, wireTransactionDetails, this.countryListNameValue);

      loaRequest.frequency = wireInfo.isTransactionFromSLOAOrSLOA ? 'On-Request' : 'One Time';
      loaRequest.processId = wireInfo.caseKey;
      loaRequest.transferId = wireInfo.transferId;
    }

    if (
      transactionType === 'internal-transfer' &&
      isInternalTransferForm(form)
    ) {
      const currentToAccount = itInfo.toAccountInfo?.accountNumber;
      const currentToAccountDistributionReason = form?.transactionDetails?.toAccounts?.find(
        account => account.toAccount === currentToAccount
      )?.iraDistributionType;

      let onetimeTotalDistIt = form.extraPrompts.frequency === 'onetimeTotalDist';
      let is401kToAccount = itInfo.toAccountInfo?.registrationType === RegTypeCode.INDIVIDUAL_401K || itInfo.toAccountInfo?.registrationType === RegTypeCode.ROTH_401K;
      const currentToAccountProperties = itsInfo?.toAccountPropertiesById[itInfo.toAccountInfo?.accountNumber];
      const defaultStateTaxPercent = this.percentFormatPipe.transform(currentToAccountProperties?.inputIraFields?.stateTaxPercent ?? itInfo.inputIraFields?.stateTaxPercent ?? '');
      const shouldIncludeDefaultZeroStateTax = itInfo.iraFields.stwProcessCode === 'N' && defaultStateTaxPercent === Constants.zeroPercent;
      if (shouldIncludeDefaultZeroStateTax) {
        itInfo.shouldShowStateTax = true;
        itInfo.iraFields = {
          ...itInfo.iraFields,
          stateTaxAmount: itInfo.iraFields.stateTaxAmount ?? Constants.zeroPercent,
        };
      }
      const transferType = form.extraPrompts.transferType;
      const isPositionsTransfer = transferType === transferTypes.POSITIONS;
      const isPositionsCashTransfer = transferType === transferTypes.POSITIONS_CASH;
      const shouldIncludeTotalToReceiveAmount = Boolean(viewAccountDetails?.isIra || accountInfo.classifications.isIra || isPositionsTransfer || isPositionsCashTransfer);
      const totalToReceiveAmountDetails = shouldIncludeTotalToReceiveAmount ? [
          new Detail(pdfLabelsSubmitDetails.totalToReceiveAmount, [
            is401kToAccount || isPositionsCashTransfer || onetimeTotalDistIt
              ? 'Available upon completion of Internal Transfer'
              : itInfo.totalWithdrawalAmount,
          ]),
        ]
        : [];
      let frequency: CodeAndDescription | undefined =
      itInfo.recurringFields.frequencyList.find(
          (frequency) => frequency.code === form.transactionDetails.recurringFieldsFormGroup?.recurringFrequency
        );
      loaRequest.frequency = frequency ? frequency.description : frequencyDict[form.extraPrompts.frequency!];
      const hasProcessDate = loaRequest.submitDetails.some(details => details.name === pdfLabelsSubmitDetails.processDate);
      const processDateDetails: Detail[] = [];
      if (!hasProcessDate && itInfo.processDate && !is401kToAccount) {
        processDateDetails.push(new Detail(pdfLabelsSubmitDetails.processDate, [itInfo.processDate.replaceAll('-', '/')]));
      } else if (!hasProcessDate && is401kToAccount && onetimeTotalDistIt) {
        processDateDetails.push(new Detail(pdfLabelsSubmitDetails.processDate, [itsInfo.processDate.replaceAll('-', '/')]));
      }
      loaRequest.submitDetails = [
        ...loaRequest.submitDetails,
          ...processDateDetails,
        ...(currentToAccountDistributionReason
          ? [
            new Detail(pdfLabelsSubmitDetails.distributionReason, [
              currentToAccountDistributionReason,
            ]),
          ]
          : []),
          ...( onetimeTotalDistIt
          ? [
            new Detail(pdfLabelsSubmitDetails.totalDistributionIncludingResiduals, [
              'Yes',
            ]),
            new Detail(pdfLabelsSubmitDetails.closeAccount, [
              this.titleCase.transform(form.extraPrompts?.closeAccount ?? 'No'),
            ])
         ]
         : [])
        ]
       loaRequest.submitDetails = [
        ...loaRequest.submitDetails,
        ...(!loaRequest.submitDetails.find(details => details.name === pdfLabelsSubmitDetails.processDate) && (itInfo.processDate || form.transactionDetails.processDate)
          ? [
            new Detail(pdfLabelsSubmitDetails.processDate, [
              (
                !itInfo.processDate || itInfo.processDate === '' ? form.transactionDetails.processDate.replaceAll('-', '/') : itInfo.processDate
              ),
            ]),
          ]
          : []),
          ...(itInfo?.recurringFields?.secondProcessDate
            ? [
              new Detail(pdfLabelsSubmitDetails.secondProcessDate, [
                new Date(itInfo?.recurringFields?.secondProcessDate).toLocaleDateString('es-pa'),
              ]),
            ]
            : []),
        ...(!loaRequest.submitDetails.find(details => details.name === pdfLabelsSubmitDetails.distributionReason) && itInfo.iraFields.distributionReason.description
          ? [
            new Detail(pdfLabelsSubmitDetails.distributionReason, [
              itInfo.iraFields.distributionReason.description,
            ]),
          ]
          : is401kToAccount && isIra && !onetimeTotalDistIt?
            [
              new Detail(pdfLabelsSubmitDetails.distributionReason, [
                itsInfo?.toAccountPropertiesById[itInfo.toAccountInfo?.accountNumber]?.inputIraFields.distributionReason?.description ?? form.transactionDetails.taxWithholdingsFormGroup.distributionReason,
              ]),
            ]
            : []),
        ...this.getItContributionReasonDetails(loaRequest, form, itInfo, itsInfo, is401kToAccount, onetimeTotalDistIt),
        ...(isIra && !is401kToAccount
          ? [
            new Detail(pdfLabelsSubmitDetails.withholdFederalTax, [
              itInfo.iraFields.isWithholdFedTax ? 'Yes' : 'No',
            ]),
            new Detail(pdfLabelsSubmitDetails.fedTax, [
              (itInfo.positionsInfo.hasSelected &&
                itInfo.iraFields.fedTaxAmount === '')
                ? itInfo.iraFields.fedTaxPercent
                : itInfo.iraFields.fedTaxAmount,
            ]),
          ]
          : is401kToAccount && isIra && !onetimeTotalDistIt ?
            [
              new Detail(pdfLabelsSubmitDetails.fedTax, [
                this.percentFormatPipe.transform(itsInfo?.toAccountPropertiesById[itInfo.toAccountInfo?.accountNumber]?.inputIraFields.fedTaxPercent) ?? 'N/A',
              ]),
            ]
            : []),
        ...((itInfo.shouldShowStateTax || shouldIncludeDefaultZeroStateTax) && !is401kToAccount
          ? [
            new Detail(pdfLabelsSubmitDetails.withholdStateTax, [
              itInfo.iraFields.isWithholdStateTax ? 'Yes' : 'No',
            ]),
            new Detail(stateTaxLabel, [
              ((itInfo.positionsInfo.hasSelected &&
                itInfo.iraFields.stateTaxAmount === '')
                ? itInfo.iraFields.stateTaxPercent
                : itInfo.iraFields.stateTaxAmount)
                || (shouldIncludeDefaultZeroStateTax ? defaultStateTaxPercent : ''),
            ]),
          ]
          : is401kToAccount && isIra && !onetimeTotalDistIt ?
            [
              new Detail(pdfLabelsSubmitDetails.stateTax, [
                this.percentFormatPipe.transform(itsInfo?.toAccountPropertiesById[itInfo.toAccountInfo?.accountNumber]?.inputIraFields.stateTaxPercent) ?? 'N/A',
              ]),
            ]
            : []),
        ...(itInfo.recurringFields?.frequency?.description
          ? [
            new Detail(pdfLabelsSubmitDetails.frequencyDescription, [
              itInfo.recurringFields.frequency.description,
            ]),
          ]
          : []),
        ...totalToReceiveAmountDetails,
        ...(!isPositionsTransfer
          ? [
            new Detail(pdfLabelsSubmitDetails.internalTransferAmount, [
              this.getInternalTransferAmount(transferType, onetimeTotalDistIt, is401kToAccount, itInfo, itsInfo),                                 
            ])
          ]
          : []),
        !!itInfo.requestedDate ?
          new Detail(pdfLabelsSubmitDetails.requestedDate, [
            this.formatDatePipe.transform(itInfo.requestedDate ?? form.transactionDetails.processDate),
          ])
          : new Detail(pdfLabelsSubmitDetails.requestedDate, [
            this.formatDatePipe.transform(is401kToAccount && onetimeTotalDistIt ? itsInfo.processDate : form.transactionDetails.processDate),
          ])
      ]
      loaRequest.toDetails = [
        new Detail(pdfLabelsToDetails.toAccount, [
          itInfo.toAccountInfo.accountNumber,
        ]),
        new Detail(
          pdfLabelsToDetails.toAccountRegistration,
          itInfo.toAccountInfo.accountRegistration
        ),
      ];
      loaRequest.toAccountId = this.hyphenPipe.transform(
        itInfo.toAccountInfo.accountNumber
      );

      if (form.prefilledFlow.name === 'edit-table') {
        loaRequest.transactionType = is401kToAccount ? Constants.journalThirdParty : viewDetails.isThirdParty
          ? Constants.journalThirdParty
          : Constants.journalFirstParty;
      } else {
        loaRequest.transactionType = is401kToAccount ? Constants.journalThirdParty : itInfo.isThirdParty
          ? Constants.journalThirdParty
          : Constants.journalFirstParty;
      }

      if (this.shouldUseNoSigPdfFlowIT(is401kToAccount, itInfo, isSubmitAndCreate)) {
        loaRequest.isFromNoSigPdfFlow = true;
      }

      if (isSubmitAndCreate) {
        // PROCESS DATE AND AMOUNT BOTH NEEDED FIRST IN CREATE SLOA DETAILS ARRAY FOR IRA AND NON IRA ACCOUNTS
        loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.processDate, [
          itInfo.processDate,
        ]));

        loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.journalAmount, [
          itInfo.journalCashAmount,
        ]));

        // BUILD REMAINING CREATE SLOA DETAILS FOR IRA ACCOUNTS
        if (isIra) {
          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.withholdFederalTax, [
            itInfo.iraFields.isWithholdFedTax ? 'Yes' : 'No',
          ]));

          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.fedTaxAmount, [
            itInfo.iraFields.fedTaxAmount,
          ]));

          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.withholdStateTax, [
            itInfo.iraFields.isWithholdStateTax ? 'Yes' : 'No',
          ]));

          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.stateTaxAmount, [
            itInfo.iraFields.stateTaxAmount || (shouldIncludeDefaultZeroStateTax ? defaultStateTaxPercent : ''),
          ]));

          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.distributionReason, [
            itInfo.iraFields.distributionReason.description,
          ]));

          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.requestedDate, [
            new Date(itInfo.requestedDate).toLocaleDateString('es-pa'),
          ]));
        }

        // BUILD REMAINING CREATE SLOA DETAILS FOR NON IRA ACCOUNTS
        else {
          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.requestedDate, [
            new Date(itInfo.requestedDate).toLocaleDateString('es-pa'),
          ]));
        }
      }
    }

    if (transactionType === 'check' && isCheckTransactionForm(form)) {
      let transactionDetails = form.transactionDetails;
      let isUseSloaOrBsloaTotalDist = (form.extraPrompts?.useStandingAuth === 'yes' || (form.extraPrompts?.useStandingAuth === 'bundled' && form.extraPrompts?.useBundledStandingAuth === 'yes')) && form.extraPrompts?.frequency === 'onetimeTotalDist';
      let onetimeTotalDistChecks = form.extraPrompts.frequency === 'onetimeTotalDist' || form.extraPrompts?.useBundledStandingAuth === 'onetimeTotalDist' || isUseSloaOrBsloaTotalDist;
      const formatPercent = new FormatPercentPipe;
      loaRequest.frequency = frequencyDict[form.extraPrompts.frequency!];
      let frequency: CodeAndDescription | undefined =
        checkInfo.frequencyList.find(
          (option) => option.code === form.transactionDetails.recurringFrequency
        );
      if (frequency) {
        loaRequest.frequency = frequency.description;
      } else if ((form.extraPrompts.useStandingAuth === 'yes' || (form.extraPrompts?.useStandingAuth === 'bundled' && form.extraPrompts?.useBundledStandingAuth === 'yes')) && !isUseSloaOrBsloaTotalDist) {
        loaRequest.frequency = 'On-Request';
      }
      else if(checkInfo?.authResponse?.canRelease && !!checkInfo?.authResponse?.draftId && !!form?.transactionDetails?.recurringFrequency){
        loaRequest.frequency = form.transactionDetails.frequencyDescription ?? '';
      }
      else if (onetimeTotalDistChecks || isUseSloaOrBsloaTotalDist) {
        loaRequest.UseOneTimeDistSloa = form.extraPrompts?.frequency === 'onetimeTotalDist' && form.extraPrompts?.useStandingAuth === yesNo.yes && form.extraPrompts?.useBundledStandingAuth !== yesNo.yes;
        loaRequest.UseOneTimeDistBsloa = form.extraPrompts?.frequency === 'onetimeTotalDist' && form.extraPrompts?.useStandingAuth === 'bundled' && form.extraPrompts?.useBundledStandingAuth === yesNo.yes;
        loaRequest.frequency = frequencyDict['onetimeTotalDist'];
      }
      let cashieringCheck: CashieringCheckInputDetails =
        checkInfo.cashieringCheckInputDetails;
      let distributionReason =
        transactionDetails &&
        transactionDetails.taxWithholdingsFormGroup &&
        transactionDetails.taxWithholdingsFormGroup.distributionReason;

      let deliveryMethod: Detail[] = [];
      let showFootnote: boolean = false;
      if (form.transactionDetails.deliveryMethod?.toLowerCase() === 'overnight') {
        deliveryMethod = [
          new Detail(pdfLabelsSubmitDetails.checkDeliveryMethod, [
            pdfLabelsSubmitDetails.overnightDeliveryMethodValue,
          ]),
          new Detail(pdfLabelsSubmitDetails.checkFee, [
            '$' + checkInfo.checkFee.toFixed(2).toString(),
          ]),
        ];
        showFootnote = true;
      }
      else if (form.transactionDetails.deliveryMethod?.toLowerCase() === 'standard') {
        deliveryMethod = [
          new Detail(pdfLabelsSubmitDetails.checkDeliveryMethod, [
            pdfLabelsSubmitDetails.standardDeliveryMethodValue,
          ])
        ];
        showFootnote = true
      }

      if (showFootnote) {
        loaRequest.messageDetails.push(
          '**' + Constants.checkProcessDateReviewCopy
        );
      }

      loaRequest.submitDetails = [
        ...loaRequest.submitDetails,
        ...(isIra
          ? [
            new Detail(pdfLabelsSubmitDetails.withholdFederalTax, [
              checkInfo.isWithholdFedTax ? 'Yes' : 'No',
            ]),
            new Detail(pdfLabelsSubmitDetails.fedTax, [
             onetimeTotalDistChecks ? formatPercent.transform(checkInfo.fedTaxAmountFormatted) : checkInfo.fedTaxAmountFormatted,
            ]),
            ...(checkInfo.shouldShowStateTax || accountInfo.classifications.isNraAccount
              ? [
                new Detail(pdfLabelsSubmitDetails.withholdStateTax, [
                  checkInfo.isWithholdStateTax ? 'Yes' : 'No',
                ]),
                new Detail(stateTaxLabel, [
                  onetimeTotalDistChecks ? formatPercent.transform(checkInfo.stateTaxAmountFormatted) : checkInfo.stateTaxAmountFormatted,
                ]),
              ]
              : []),
            new Detail(pdfLabelsSubmitDetails.totalWithdrawalAmount, [
              checkInfo.totalWithdrawalAmount,
            ]),
          ]
          : []),
        ...(distributionReason &&
          distributionReason !== '' &&
          distributionReason !== null
          ? [
            new Detail(pdfLabelsSubmitDetails.distributionReason, [
              distributionReason,
            ]),
          ]
          : []),
        ...(checkInfo.checkReason &&
          checkInfo.checkReason !== '' &&
          checkInfo.checkReason !== null
          ? [
            new Detail(pdfLabelsSubmitDetails.withdrawalReason, [
              checkInfo.checkReason,
            ]),
          ]
          : onetimeTotalDistChecks ? [
            new Detail(pdfLabelsSubmitDetails.withdrawalReason, [
              checkInfo.checkReasonMap?.find(element => element.code === transactionDetails.checkReason!)?.description ?? '',
            ]),
          ] : []),
        ...(checkInfo.recurringFields?.frequency?.description
          ? [
            new Detail(pdfLabelsSubmitDetails.frequencyDescription, [
              checkInfo.recurringFields.frequency.description,
            ]),
          ]
          : []),
        ...deliveryMethod,
        new Detail((showFootnote ? '**' : '') + pdfLabelsSubmitDetails.processDate, [
          new Date(checkInfo.processDate).toLocaleDateString('es-pa'),
        ]),
          ...((checkInfo?.cashieringCheckInputDetails?.secondProcessDate || checkInfo?.recurringFields?.secondProcessDate) && checkInfo.cashieringCheckInputDetails.secondProcessDate !== '01-01-0001'
            ? [
              new Detail(pdfLabelsSubmitDetails.secondProcessDate, [
                new Date(checkInfo.cashieringCheckInputDetails.secondProcessDate || checkInfo?.recurringFields?.secondProcessDate).toLocaleDateString('es-pa'),
              ]),
            ]
            : []),
        ...( onetimeTotalDistChecks
          ? [
            new Detail(pdfLabelsSubmitDetails.closeAccount, [
              this.titleCase.transform(form.extraPrompts?.closeAccount ?? ''),
            ]),
         ]
         : []),
        new Detail(pdfLabelsSubmitDetails.checkAmount,
          [checkInfo.amount ? checkInfo.amount : onetimeTotalDistChecks ? form.transactionDetails.amount ?? '' : Constants.zeroAmount]
        ),
        new Detail(pdfLabelsSubmitDetails.requestedDate, [
          onetimeTotalDistChecks ? new Date(checkInfo.processDate).toLocaleDateString('es-pa')
          :  new Date(checkInfo.requestedDate).toLocaleDateString('es-pa'),
        ]),
        ...(cashieringCheck.isRecurringTransaction
          ? [
            new Detail(pdfLabelsSubmitDetails.nextProcessDate, [
              new Date(cashieringCheck.nextProcessDate).toLocaleDateString(
                'es-pa'
              ),
            ]),
          ]
          : []),
      ];

      if(onetimeTotalDistChecks){
        loaRequest.checksIaInformationAccess = {
          investmentCalendarYear: form.extraPrompts?.taxFormsDateInputControl ?? '',
          investmentCalendarMonth: form.extraPrompts?.accountStatementsDateInputControl ?? '',
          investmentCalendarDate: this.formatDatePipe.transform(form.extraPrompts?.tradeConfirmationsDateInputControl ?? '') ?? ''
        }
      }

      if (isSubmitAndCreate) {
        loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.processDate, [
          new Date(checkInfo.processDate).toLocaleDateString('es-pa')]));
        loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.checkAmount, [
          !!checkInfo.amount ? checkInfo.amount : Constants.zeroAmount]));

        if (isIra) {
          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.withholdFederalTax, [
            checkInfo.isWithholdFedTax ? 'Yes' : 'No']));
          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.fedTax, [
            checkInfo.fedTaxAmountFormatted]));
          if(checkInfo.shouldShowStateTax) {
            loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.withholdStateTax, [
              checkInfo.isWithholdStateTax ? 'Yes' : 'No']));
            loaRequest.createSloaDetails.push(new Detail(stateTaxLabel, [
              checkInfo.stateTaxAmountFormatted]));
          }
          if(!!distributionReason) {
            loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.distributionReason, [
              distributionReason]));
          }
          if(checkInfo?.cashieringCheckInputDetails?.secondProcessDate) {
            loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.secondProcessDate, [
              new Date(checkInfo.cashieringCheckInputDetails.secondProcessDate).toLocaleDateString('es-pa')
            ]));
          }
        }
        if(checkInfo.checkReason) {
          loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.withdrawalReason, [
            checkInfo.checkReason]));
        }
        loaRequest.createSloaDetails.push(new Detail(pdfLabelsSubmitDetails.requestedDate, [
          new Date(checkInfo.requestedDate).toLocaleDateString('es-pa')]));
      }

      let checkMemo: string = checkInfo.memo?.trim();
      loaRequest.toDetails = [
        new Detail(pdfLabelsToDetails.payee, isUseSloaOrBsloaTotalDist ? checkInfo?.defaultPayee : checkInfo.payee),
        ...(!!checkMemo ? [new Detail(pdfLabelsToDetails.memo, [checkInfo.memo])] : []),
        new Detail(
          pdfLabelsToDetails.recipientAddress,
          (form.transactionDetails.recipient === checkRecipientTypeDict.addressofrecord || ((transactionDetails.recipient === checkRecipientTypeDict.addressdomestic || transactionDetails.recipient === checkRecipientTypeDict.addressforeign) && (form.extraPrompts?.useStandingAuth === yesNo.yes || form.extraPrompts?.useBundledStandingAuth === yesNo.yes))) && !!checkInfo.defaultRecipient
            ? checkInfo.defaultRecipient
            : this.getDeliveryAddress(transactionDetails, checkInfo.deliveryAddress)
        )
      ];
      let isBundledOneTimeTotalDist = form.extraPrompts?.useBundledStandingAuth === 'onetimeTotalDist';
      loaRequest.transactionType =
        form.transactionDetails.payeeType === 'thirdParty'
          ? Constants.checkThirdParty
          : Constants.checkFirstParty;
    }

    if (!selectedCostBasisMethod && itInfo?.positionsInfo?.costBasisMethodSelected) {
      selectedCostBasisMethod = itInfo.positionsInfo.costBasisMethodSelected
    }
    if (!defaultCostBasisMethod && itInfo?.positionsInfo?.defaultCostBasisMethod) {
      defaultCostBasisMethod = itInfo?.positionsInfo?.defaultCostBasisMethod
    }

    if ( selectedCostBasisMethod && defaultCostBasisMethod) {
      let costBasisDetailDefault = new Detail('Cost Basis Method Default', [
         positionConstants.costBasisMap.get(defaultCostBasisMethod) ?? defaultCostBasisMethod,
      ]);
      let costBasisDetailSelected = new Detail('Cost Basis Method Selected', [
        positionConstants.costBasisMap.get(selectedCostBasisMethod) ?? selectedCostBasisMethod,
      ]);
      if (costBasisDetailSelected.values[0].includes('Tax Lot Optimizer')) {
        costBasisDetailSelected.values[0] = costBasisDetailSelected.values[0] + " (Executed with Highest-cost lot)";
        costBasisDetailSelected.values[1] = positionConstants.taxLotOptimizerWarningMessage;
      }
      loaRequest.submitDetails.push(costBasisDetailDefault);
      loaRequest.submitDetails.push(costBasisDetailSelected);
      loaRequest.selectedPositionsList = itInfo.rowData.length === 0 ? this.createSelectedPositionsListView(itInfo.positionsInfo.list) : this.createSelectedPositionsList(itInfo.rowData);
    }

    if (loaRequest.selectedPositionsList.length === 0 && itInfo.positionsInfo.list.length !== 0) {
      loaRequest.selectedPositionsList = this.createSelectedPositionsListView(itInfo.positionsInfo.list)
    }

    if (submitAndCreateStatus?.hasError && submitAndCreateStatus.transactionFailed) {
      loaRequest.submitDetails = [];
    }

    loaRequest.registrationType = accountInfo.registrationType;

    if ((form as ItForm)?.extraPrompts?.closeAccount) {
        var extraPrompts = (form as ItForm).extraPrompts
        loaRequest.journalsIaInformationAccess = {
            investmentCalendarYear: extraPrompts.taxFormsDateInputControl ?? '',
            investmentCalendarMonth: extraPrompts.accountStatementsDateInputControl?.replaceAll('-','/') ?? '',
            investmentCalendarDate: extraPrompts.tradeConfirmationsDateInputControl?.replaceAll('-','/') ?? ''
        }
      }

    if (useTestTrustDigitalToPdfFlow) {
      loaRequest.formsFlowPdf = true;
      if (transactionType === 'check' && isCheckTransactionForm(form)) {
        loaRequest.transactionType = 'Check';
      } else if (transactionType === 'internal-transfer' && isInternalTransferForm(form)) {
        loaRequest.transactionType = 'Journal';
      }
    }

    return loaRequest;
  }

  getToDetailsLoaRequest(loaRequest: LetterOfAuthorizationRequest, wireTransactionDetails: WireTransactionDetails) {
    return loaRequest.toDetails = [
      ...loaRequest.toDetails,
      new Detail(pdfLabelsToDetails.beneficiaryAccountNumber, [
        wireTransactionDetails.toAccount
      ]),
      new Detail(pdfLabelsToDetails.beneficiaryNameLabel, [
        wireTransactionDetails.receivingAccountHolderName1,
        wireTransactionDetails.receivingAccountHolderName2,
      ]),
      new Detail(pdfLabelsToDetails.beneficiaryAddress, [
        wireTransactionDetails.receivingAccountHolderAddress1,
        wireTransactionDetails.receivingAccountHolderAddress2,
      ]),
      new Detail(pdfLabelsToDetails.beneficiaryCountry, [
        wireTransactionDetails.receivingAccountHolderCountry,
      ]),
      new Detail(pdfLabelsToDetails.receivingBankAbaNumber, [
        wireTransactionDetails.aba
      ]),
      new Detail(pdfLabelsToDetails.receivingBankName, [
        wireTransactionDetails.receivingBankName1,
        wireTransactionDetails.receivingBankName2,
      ]),
      new Detail(pdfLabelsToDetails.receivingBankAddress, [
        wireTransactionDetails.receivingBankAddress1,
        wireTransactionDetails.receivingBankAddress2,
      ]),
      new Detail(pdfLabelsToDetails.receivingBankCountry, [
        wireTransactionDetails.receivingBankCountry,
      ]),
      ...(wireTransactionDetails.intermediarySelection === 'yes'
        ? [
          new Detail(pdfLabelsToDetails.intermediaryAccountNumber, [
            wireTransactionDetails.intermediaryAccountNumber,
          ]),
          new Detail(pdfLabelsToDetails.intermediaryAddress, [
            wireTransactionDetails.intermediaryAddress,
            wireTransactionDetails.intermediaryAddressCont,
          ]),
          new Detail(pdfLabelsToDetails.intermediaryName, [
            wireTransactionDetails.intermediaryName,
            wireTransactionDetails.intermediaryNameCont
          ]),
          new Detail(pdfLabelsToDetails.intermediaryCountry, [
            wireTransactionDetails.intermediaryCountry,
          ]),
          new Detail(pdfLabelsToDetails.intermediaryPhone, [
            wireTransactionDetails.intermediaryPhone,
          ]),
        ]
        : []),
      ...(wireTransactionDetails.creditToSelection === 'yes'
          ? [
            new Detail(pdfLabelsToDetails.creditToAccountNumber, [
              wireTransactionDetails.creditToAccountNumber,
            ]),
            new Detail(pdfLabelsToDetails.creditToWireName, [
              wireTransactionDetails.creditToName,
              wireTransactionDetails.creditToNameCont
            ]),
            new Detail(pdfLabelsToDetails.creditToIntermediaryAddress, [
              wireTransactionDetails.creditToAddress,
              wireTransactionDetails.creditToAddressCont,
            ]),
            new Detail(pdfLabelsToDetails.creditToIntermediaryCountry, [
              wireTransactionDetails.creditToCountry,
            ]),
            new Detail(pdfLabelsToDetails.creditToIntermediaryPhone, [
              wireTransactionDetails.creditToPhone,
          ]),
        ]
        : []),
    ];
  }

  getIsoToDetailsLoaRequest(loaRequest: LetterOfAuthorizationRequest, wireTransactionDetails: WireTransactionDetails, countryList: OptionDropdownSelect[]) {
    loaRequest.toDetails = [];
    let isIntermediary = wireTransactionDetails.intermediarySelection === 'yes';
    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.intermediaryBankAbaNumber : pdfLabelsToDetails.receivingBankAbaNumberOneStep, [
      wireTransactionDetails.aba
    ]));
    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.intermediaryBankName : pdfLabelsToDetails.receivingBankNameOneStep, [
      wireTransactionDetails.receivingBankName,
    ]));
    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.intermediaryBankAddress : pdfLabelsToDetails.receivingBankAddressOneStep, [
      wireTransactionDetails.receivingBankCity + ' ' + wireTransactionDetails.receivingBankState
    ]));
    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.intermediaryBankCountry : pdfLabelsToDetails.receivingBankCountryOneStep, [
      CountryDescriptionFromCode(wireTransactionDetails.receivingBankCountry, countryList),
    ]));
    if(isIntermediary){
      loaRequest.toDetails.push(new Detail(pdfLabelsToDetails.recipientBankAccountNumber, [
        wireTransactionDetails.intermediaryAccountNumber
      ]));
      loaRequest.toDetails.push(new Detail(pdfLabelsToDetails.recipientBankName, [
        wireTransactionDetails.intermediaryName
      ]));
      loaRequest.toDetails.push(new Detail(pdfLabelsToDetails.recipientAddress, [
        this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.intermediaryStreetNumber ? (wireTransactionDetails.intermediaryStreetNumber + " " + wireTransactionDetails.intermediaryStreetName) : wireTransactionDetails.intermediaryStreetName),
        this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.intermediaryApartmentSuiteEtc, wireTransactionDetails.intermediaryDepartment, wireTransactionDetails.intermediaryBuildingName, wireTransactionDetails.intermediaryFloor) +
        this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.intermediaryDepartment, wireTransactionDetails.intermediaryBuildingName, wireTransactionDetails.intermediaryFloor) +
        this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.intermediaryBuildingName, wireTransactionDetails.intermediaryFloor) +
        this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.intermediaryFloor),
        this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.intermediaryTownLocationName, wireTransactionDetails.intermediaryCity, wireTransactionDetails.intermediaryDistrict, wireTransactionDetails.intermediaryState, wireTransactionDetails.intermediaryZip) +
        this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.intermediaryCity, wireTransactionDetails.intermediaryDistrict, wireTransactionDetails.intermediaryState, wireTransactionDetails.intermediaryZip) +
        this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.intermediaryDistrict, wireTransactionDetails.intermediaryState, wireTransactionDetails.intermediaryZip) +
        this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.intermediaryState, wireTransactionDetails.intermediaryZip) +
        this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.intermediaryZip)
      ]));
      loaRequest.toDetails.push(new Detail(pdfLabelsToDetails.recipientBankCountry, [
        CountryDescriptionFromCode(wireTransactionDetails.intermediaryCountry, countryList),
      ]));
    }
    if(loaRequest.submitDetails.filter(x => x.name == "Taxpayer ID").length == 0 && loaRequest.createSloaDetails.filter(x => x.name == "Taxpayer ID").length == 0) {
      loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.finalRecipientAccountNumber : pdfLabelsToDetails.recipientAccountNumberOneStep, [
        wireTransactionDetails.toAccount
      ]));
    }
    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.finalRecipientNameLabel : pdfLabelsToDetails.recipientNameOneStepLabel, [
      wireTransactionDetails.name
    ]));
    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.finalRecipientAddress : pdfLabelsToDetails.recipientAddressOneStep, [
      this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.receivingAccountHolderStreetNumber ? (wireTransactionDetails.receivingAccountHolderStreetNumber + " " + wireTransactionDetails.receivingAccountHolderStreetName) : wireTransactionDetails.receivingAccountHolderStreetName),
      this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.receivingAccountHolderApartmentSuiteEtc, wireTransactionDetails.receivingAccountHolderDepartment, wireTransactionDetails.receivingAccountHolderBuildingName, wireTransactionDetails.receivingAccountHolderFloor) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.receivingAccountHolderDepartment, wireTransactionDetails.receivingAccountHolderBuildingName, wireTransactionDetails.receivingAccountHolderFloor) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.receivingAccountHolderBuildingName, wireTransactionDetails.receivingAccountHolderFloor) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.receivingAccountHolderFloor),
      this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.receivingAccountHolderTownLocationName, wireTransactionDetails.receivingAccountHolderCity, wireTransactionDetails.receivingAccountHolderDistrict, wireTransactionDetails.receivingAccountHolderState, wireTransactionDetails.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.receivingAccountHolderCity, wireTransactionDetails.receivingAccountHolderDistrict, wireTransactionDetails.receivingAccountHolderState, wireTransactionDetails.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.receivingAccountHolderDistrict, wireTransactionDetails.receivingAccountHolderState, wireTransactionDetails.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.receivingAccountHolderState, wireTransactionDetails.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireTransactionDetails.receivingAccountHolderZip)
    ]));
    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.finalRecipientCountry : pdfLabelsToDetails.recipientCountryOneStep, [
      CountryDescriptionFromCode(wireTransactionDetails.receivingAccountHolderCountry, countryList),
    ]));
    return loaRequest.toDetails;
  }

  getToDetailsLoaRequestSA(loaRequest: LetterOfAuthorizationRequest, wireStandingAuthDetails: WireStandingAuthDetails) {
    return loaRequest.toDetails = [
      ...loaRequest.toDetails,
      new Detail(pdfLabelsToDetails.beneficiaryAccountNumber, [
        wireStandingAuthDetails.toAccount
      ]),
      new Detail(pdfLabelsToDetails.beneficiaryNameLabel, [
        wireStandingAuthDetails.receivingAccountHolderName1,
        wireStandingAuthDetails.receivingAccountHolderName2,
      ]),
      new Detail(pdfLabelsToDetails.beneficiaryAddress, [
        wireStandingAuthDetails.receivingAccountHolderAddress1,
        wireStandingAuthDetails.receivingAccountHolderAddress2,
      ]),
      new Detail(pdfLabelsToDetails.beneficiaryCountry, [
        wireStandingAuthDetails.receivingAccountHolderCountry,
      ]),
      new Detail(pdfLabelsToDetails.receivingBankAbaNumber, [
        wireStandingAuthDetails.aba
      ]),
      new Detail(pdfLabelsToDetails.receivingBankName, [
        wireStandingAuthDetails.receivingBankName1,
        wireStandingAuthDetails.receivingBankName2,
      ]),
      new Detail(pdfLabelsToDetails.receivingBankAddress, [
        wireStandingAuthDetails.receivingBankAddress1,
        wireStandingAuthDetails.receivingBankAddress2,
      ]),
      new Detail(pdfLabelsToDetails.receivingBankCountry, [
        wireStandingAuthDetails.receivingBankCountry,
      ]),
      ...(wireStandingAuthDetails.intermediarySelection === 'yes'
        ? [
          new Detail(pdfLabelsToDetails.intermediaryAccountNumber, [
            wireStandingAuthDetails.intermediaryAccountNumber,
          ]),
          new Detail(pdfLabelsToDetails.intermediaryAddress, [
            wireStandingAuthDetails.intermediaryAddress,
            wireStandingAuthDetails.intermediaryAddressCont,
          ]),
          new Detail(pdfLabelsToDetails.intermediaryName, [
            wireStandingAuthDetails.intermediaryName,
            wireStandingAuthDetails.intermediaryNameCont,
          ]),
          new Detail(pdfLabelsToDetails.intermediaryCountry, [
            wireStandingAuthDetails.intermediaryCountry,
          ]),
          new Detail(pdfLabelsToDetails.intermediaryPhone, [
            wireStandingAuthDetails.intermediaryPhone,
          ]),
        ]
        : []),
    ];
  }

  getIsoToDetailsLoaRequestSA(loaRequest: LetterOfAuthorizationRequest, wireStandingAuthDetails: WireStandingAuthDetails, countryList: OptionDropdownSelect[]) {
    let isIntermediary = wireStandingAuthDetails.intermediarySelection === 'yes';

    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.intermediaryBankAbaNumber : pdfLabelsToDetails.receivingBankAbaNumberOneStep, [
      wireStandingAuthDetails.aba
    ]));
    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.intermediaryBankName : pdfLabelsToDetails.receivingBankNameOneStep, [
      wireStandingAuthDetails.receivingBankName1,
    ]));
    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.intermediaryBankAddress : pdfLabelsToDetails.receivingBankAddressOneStep, [
      wireStandingAuthDetails.receivingBankCity + ' ' + wireStandingAuthDetails.receivingBankState
    ]));
    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.intermediaryBankCountry : pdfLabelsToDetails.receivingBankCountryOneStep, [
      CountryDescriptionFromCode(wireStandingAuthDetails.receivingBankCountry, countryList),
    ]));
    if(isIntermediary){
      loaRequest.toDetails.push(new Detail(pdfLabelsToDetails.recipientBankAccountNumber, [
          wireStandingAuthDetails.intermediaryAccountNumber
      ]));
      loaRequest.toDetails.push(new Detail(pdfLabelsToDetails.recipientBankName, [
        wireStandingAuthDetails.intermediaryName
      ]));
      loaRequest.toDetails.push(new Detail(pdfLabelsToDetails.recipientAddress, [
        this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.intermediaryStreetNumber ? (wireStandingAuthDetails.intermediaryStreetNumber + " " + wireStandingAuthDetails.intermediaryStreetName) : wireStandingAuthDetails.intermediaryStreetName),
        this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.intermediaryApartmentSuiteEtc, wireStandingAuthDetails.intermediaryDepartment, wireStandingAuthDetails.intermediaryBuildingName, wireStandingAuthDetails.intermediaryFloor) +
        this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.intermediaryDepartment, wireStandingAuthDetails.intermediaryBuildingName, wireStandingAuthDetails.intermediaryFloor) +
        this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.intermediaryBuildingName, wireStandingAuthDetails.intermediaryFloor) +
        this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.intermediaryFloor),
        this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.intermediaryCity, wireStandingAuthDetails.intermediaryState, wireStandingAuthDetails.intermediaryZip) +
        this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.intermediaryState, wireStandingAuthDetails.intermediaryZip) +
        this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.intermediaryZip)
      ]));
      loaRequest.toDetails.push(new Detail(pdfLabelsToDetails.recipientBankCountry, [
        CountryDescriptionFromCode(wireStandingAuthDetails.intermediaryCountry, countryList),
      ]));
    }
    if (loaRequest.createSloaDetails.filter(x => x.name == "Taxpayer ID").length == 0) {
      loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.finalRecipientAccountNumber : pdfLabelsToDetails.recipientAccountNumberOneStep, [
        wireStandingAuthDetails.toAccount
      ]));
    }
    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.finalRecipientNameLabel : pdfLabelsToDetails.recipientNameOneStepLabel, [
      wireStandingAuthDetails.name
    ]));
    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.finalRecipientAddress : pdfLabelsToDetails.recipientAddressOneStep, [
      this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.receivingAccountHolderStreetNumber ? (wireStandingAuthDetails.receivingAccountHolderStreetNumber + " " + wireStandingAuthDetails.receivingAccountHolderStreetName) : wireStandingAuthDetails.receivingAccountHolderStreetName),
      this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.receivingAccountHolderApartmentSuiteEtc, wireStandingAuthDetails.receivingAccountHolderDepartment, wireStandingAuthDetails.receivingAccountHolderBuildingName, wireStandingAuthDetails.receivingAccountHolderFloor) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.receivingAccountHolderDepartment, wireStandingAuthDetails.receivingAccountHolderBuildingName, wireStandingAuthDetails.receivingAccountHolderFloor) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.receivingAccountHolderBuildingName, wireStandingAuthDetails.receivingAccountHolderFloor) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.receivingAccountHolderFloor),
      this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.receivingAccountHolderTownLocationName, wireStandingAuthDetails.receivingAccountHolderCity, wireStandingAuthDetails.receivingAccountHolderDistrict, wireStandingAuthDetails.receivingAccountHolderState, wireStandingAuthDetails.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.receivingAccountHolderCity, wireStandingAuthDetails.receivingAccountHolderDistrict, wireStandingAuthDetails.receivingAccountHolderState, wireStandingAuthDetails.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.receivingAccountHolderDistrict, wireStandingAuthDetails.receivingAccountHolderState, wireStandingAuthDetails.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.receivingAccountHolderState, wireStandingAuthDetails.receivingAccountHolderZip) +
      this.formatIsoAddressFieldsService.getFieldsValue(wireStandingAuthDetails.receivingAccountHolderZip)
    ]));
    loaRequest.toDetails.push(new Detail(isIntermediary ? pdfLabelsToDetails.finalRecipientCountry : pdfLabelsToDetails.recipientCountryOneStep, [
      CountryDescriptionFromCode(wireStandingAuthDetails.receivingAccountHolderCountry, countryList),
    ]));
    return loaRequest.toDetails;
  }

  getDeliveryAddress(transactionDetails: CheckTransactionDetails, deliveryAddress: string[]) {
    return transactionDetails.country === 'US' ? deliveryAddress.concat('UNITED STATES') : deliveryAddress;
  }

  shouldUseNoSigPdfFlowIT(
    is401kToAccount: boolean,
    itInfo: InternalTransferInfo,
    isSubmitAndCreate: boolean
  ): boolean {
    if (is401kToAccount) {
      return false; //Signatures should be required for i401k account for all roles because default value isthridparty is true
    }
    else {
      return itInfo.isIdentical && itInfo.hasWpfaRole && !isSubmitAndCreate;
    }
  }

  shouldUseNoSigPdfFlow(
    isSubmitAndCreate: boolean,
    transactionDetails: CheckTransactionDetails,
    isOneTimeTotalDist: boolean,
    accountInfo: AccountInfo,
    isBundledOneTimeTotalDist: boolean,
    isUseSloaOrBsloaTotalDist: boolean
  ): boolean {
    const isFirstPartyRecipient = transactionDetails.recipient === 'firstParty';
    const isFirstPartyPayee = transactionDetails.payeeType === 'firstParty';
    const hasNdfaOrLpfaRole = !!(accountInfo.pfaDetails?.hasNdfaRole || accountInfo.pfaDetails?.hasLpfaRole);
    const hasWpfaRole = !!accountInfo.pfaDetails?.hasWpfaRole;
    // For onetimeTotalDistChecks, require WPFA and not NDFA/LPFA
    const allowNoSigForOnetimeTotalDist = isOneTimeTotalDist && hasWpfaRole && !hasNdfaOrLpfaRole;
    //The authorization form should never require signatures (since it's use SLOA) regardless of being 1st/3rd party
    if(isUseSloaOrBsloaTotalDist){
      return true;
    }

    // For bundled onetimeTotalDistChecks everytime no-sign PDF should be generated
    if (isBundledOneTimeTotalDist) {
      return !isSubmitAndCreate;
    }

    return (
      !isSubmitAndCreate &&
      isFirstPartyRecipient &&
      isFirstPartyPayee &&
      (!isOneTimeTotalDist || allowNoSigForOnetimeTotalDist)
    );
  }

  createSelectedPositionsList(positions: TableColumn) {
    let lotDetail: Array<any> = [];
    lotDetail = positions.map((position: any) => {
      if (position['Shares to move'] !== '') {
        //positionSign is used to know when a position is a short and as a result display estimated market value as a negative
        let positionSign = Math.sign(Number(position['Available shares'].split(' ')[0].replaceAll(',',''))) ?? 1
        return {
          selectedQuantity: Number(position['Shares to move']).toLocaleString("en-US"),
          symbol: position['Symbol'],
          fullName: position['Name'],
          marketValue:
            Number(position['Shares to move']) *
            Number(position['Current price/share'].replace('$', '')) *
            Number(position['valMultiplier']) *
            positionSign,
        };
      }
    });
    lotDetail = lotDetail.filter(Boolean);
    lotDetail.sort((positionA, positionB) => positionA.symbol.localeCompare(positionB.symbol));
    return lotDetail;
  }

  createSelectedPositionsListView(positions: TableColumn) {
    let positionsDetail: Array<any> = [];
    positionsDetail = positions.map((position: any) => {
      return {
        selectedQuantity: position['selectedQuantity'],
        symbol: position['symbol'],
        fullName: position['fullName'],
        marketValue: position['selectedMarketValue'],
      };

    });
    positionsDetail.sort((positionA, positionB) => positionA.symbol.localeCompare(positionB.symbol));
    return positionsDetail;
  }

  generateStandingAuthLOAFormRequestFromViewPage(
    form: TransactionForm,
    accountInfo: State,
    wireInfo: WireInfo,
    internalTransferInfo: InternalTransferInfo,
    checkInfo: CheckInfo,
    viewDetails: ViewDetails,
    stateCode: string
  ): LetterOfAuthorizationRequest {
    let transactionType = viewDetails.transactionType;
    transactionType = transactionType === 'WireSloa' ? 'wire' : transactionType;
    let transactionTypeCapitalized = transactionType?.charAt(0)?.toUpperCase() + transactionType?.slice(1);
    let isIra = accountInfo.classifications.isIra;

    let stateTaxLabel = pdfLabelsSubmitDetails.stateTax;
    if (!accountInfo.classifications.isNraAccount && stateCode) {
      stateTaxLabel = `State Tax (${stateCode}):`
    };

    let loaRequest = new LetterOfAuthorizationRequest({
      subAccountId: viewDetails.subAccountId || accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount.toString().padStart(8, '0'),
      frequency: 'One Time',
      registrationType: accountInfo.registrationType,
      transactionType: transactionTypeCapitalized,
      state: stateCode
    });

    if (transactionType === 'check') {
      let standingAuthDetails = (form as CheckForm).transactionDetails;
      let distributionReason = viewDetails.distributionReason.description;

      loaRequest.transactionType = standingAuthDetails.payeeType === 'thirdParty' ? Constants.checkThirdParty : Constants.checkFirstParty;

      let checkMemo: string = checkInfo.memo?.trim();
      loaRequest.toDetails = [
        new Detail(pdfLabelsToDetails.payee, checkInfo.payee),
        ...(!!checkMemo ? [new Detail(pdfLabelsToDetails.memo, [checkInfo.memo])] : []),
        new Detail(pdfLabelsToDetails.recipientAddress,
          standingAuthDetails.recipient === 'firstParty' && !!checkInfo.defaultRecipient
            ? checkInfo.defaultRecipient
            : checkInfo.deliveryAddress),
      ];

      loaRequest.createSloaDetails = [
        ...(isIra ? [
          new Detail(pdfLabelsSubmitDetails.withholdFederalTax, [checkInfo.isWithholdFedTax ? 'Yes' : 'No']),
          new Detail(pdfLabelsSubmitDetails.fedTax, [checkInfo.fedTaxRate]),
          ...(checkInfo.shouldShowStateTax || accountInfo.classifications.isNraAccount ? [
            new Detail(pdfLabelsSubmitDetails.withholdStateTax, [checkInfo.isWithholdStateTax ? 'Yes' : 'No']),
            new Detail(stateTaxLabel, [checkInfo.stateTaxRate])] : [])] : []),
        ...(distributionReason && distributionReason !== '' && distributionReason !== null ? [
          new Detail(pdfLabelsSubmitDetails.distributionReason, [distributionReason])] : []),
        ...(checkInfo.checkReason && checkInfo.checkReason !== '' && checkInfo.checkReason !== null ?
          [new Detail(pdfLabelsSubmitDetails.withdrawalReason, [checkInfo.checkReason])] : [new Detail(pdfLabelsSubmitDetails.withdrawalReason, [])]),
        new Detail(pdfLabelsSubmitDetails.requestedDate, [new Date(checkInfo.requestedDate).toLocaleDateString('es-pa')]),
      ]
    }

    if (transactionType === 'internal-transfer') {
       let is401kToAccount = internalTransferInfo.toAccountInfo?.registrationType === RegTypeCode.INDIVIDUAL_401K || internalTransferInfo.toAccountInfo?.registrationType === RegTypeCode.ROTH_401K;
      loaRequest.transactionType =  is401kToAccount ? Constants.journalThirdParty : internalTransferInfo.isThirdParty ? Constants.journalThirdParty : Constants.journalFirstParty;

      const defaultStateTaxPercent = this.percentFormatPipe.transform(internalTransferInfo.iraFields.stateTaxPercent ?? '');
      const shouldIncludeDefaultZeroStateTax = internalTransferInfo.iraFields.stwProcessCode === 'N' && defaultStateTaxPercent === Constants.zeroPercent;

      loaRequest.toDetails = [
        new Detail(pdfLabelsToDetails.toAccount, [internalTransferInfo.toAccountInfo.accountNumber]),
        new Detail(pdfLabelsToDetails.toAccountRegistration, internalTransferInfo.toAccountInfo.accountRegistration)
      ];

      loaRequest.createSloaDetails = [
        ...(isIra ? [
          new Detail(pdfLabelsSubmitDetails.withholdFederalTax, [internalTransferInfo.iraFields.isWithholdFedTax ? 'Yes' : 'No']),
          new Detail(pdfLabelsSubmitDetails.fedTax, [internalTransferInfo.iraFields.fedTaxAmount]),
          ...(internalTransferInfo.shouldShowStateTax || shouldIncludeDefaultZeroStateTax ? [
            new Detail(pdfLabelsSubmitDetails.withholdStateTax, [internalTransferInfo.iraFields.isWithholdStateTax ? 'Yes' : 'No']),
            new Detail(stateTaxLabel, [internalTransferInfo.iraFields.stateTaxAmount || (shouldIncludeDefaultZeroStateTax ? defaultStateTaxPercent : '')])] : []),
          new Detail(pdfLabelsSubmitDetails.distributionReason, [])] : []),
        new Detail(pdfLabelsSubmitDetails.requestedDate, [this.formatDatePipe.transform(internalTransferInfo.requestedDate)]),
      ]

      loaRequest.toAccountId = this.hyphenPipe.transform(internalTransferInfo.toAccountInfo.accountNumber);
    }

    if (transactionType === 'wire' || transactionType === 'WireSloa') {
      let wireStandingAuthDetails = (form as WireForm).transactionDetails;

      // Find requestedDate value
      const requestedDateValue = wireStandingAuthDetails.requestedDate || '';

      loaRequest.createSloaDetails = [
        ...(!wireInfo.isIrsAba
          ? [new Detail(pdfLabelsSubmitDetails.additionalWireInstructions, [wireStandingAuthDetails.transmissionNotes?.toUpperCase()])]
          : []),

        // If IRS ABA, add tax fields before requestedDate
        ...(wireInfo.isIrsAba
          ? [
            new Detail(pdfLabelsSubmitDetails.transmissionNotesIrs, [wireInfo.taxRemittance?.additionalInfo]),
            new Detail(pdfLabelsSubmitDetails.taxId, [this.maskAccountNumberPipe.transform(wireInfo.taxRemittance?.taxId)]),
            new Detail(pdfLabelsSubmitDetails.taxTypeCode, [wireInfo.taxRemittance?.taxTypeCode]),
          ]
          : []),
        new Detail(pdfLabelsSubmitDetails.requestedDate, [requestedDateValue]),
        // IRA fields after requestedDate if needed
        ...(isIra
          ? [
            new Detail(pdfLabelsSubmitDetails.withholdFederalTax, [wireInfo.withholdFedTax || accountInfo.classifications.isNraAccount ? 'Yes' : 'No']),
            new Detail(pdfLabelsSubmitDetails.fedTax, [wireInfo.fedTaxPercent]),
            ...(wireInfo.stateHasTaxes || accountInfo.classifications.isNraAccount
              ? [
                new Detail(pdfLabelsSubmitDetails.withholdStateTax, [wireInfo.withholdStateTax ? 'Yes' : 'No']),
                new Detail(pdfLabelsSubmitDetails.stateTax, [wireInfo.stateTaxPercent]),
              ]
              : []),
          ]
          : []),
      ];
      
      loaRequest.toDetails = this.getIsoToDetailsLoaRequest(loaRequest, wireStandingAuthDetails, this.countryListNameValue);
      loaRequest.frequency = 'On-Request';
      loaRequest.processId = wireInfo.caseKey;
      loaRequest.transferId = wireInfo.transferId;
    }

    return loaRequest;
  }

  getItContributionReasonDetails(
    loaRequest: LetterOfAuthorizationRequest,
    form: TransactionForm,
    itInfo: InternalTransferInfo,
    itsInfo: InternalTransfersInfo,
    is401kToAccount: boolean,
    onetimeTotalDistIt: boolean
  ): Detail[] {
    const isContributionReasonPresent = !!loaRequest.submitDetails.find(
      details => details.name === pdfLabelsSubmitDetails.contributionReason
    );

    if (isContributionReasonPresent) {
      return [];
    }

    const formIraContributionType = (form as ItForm).transactionDetails?.toAccounts?.[0]?.iraContributionType;
    const itInfoContributionDescription = itInfo.iraContributionType?.description || formIraContributionType;

    const contributionValue = (is401kToAccount && !onetimeTotalDistIt)
      ? (itsInfo?.toAccountPropertiesById[itInfo.toAccountInfo?.accountNumber]?.iraContributionType?.description || itInfoContributionDescription || 'N/A')
      : itInfoContributionDescription;

    if (!contributionValue) {
      return [];
    }

    return [new Detail(pdfLabelsSubmitDetails.contributionReason, [contributionValue])];
  }

  getInternalTransferAmount(transferType: string | undefined, onetimeTotalDistIt: boolean, is401kToAccount: boolean, itInfo: InternalTransferInfo, itsInfo: InternalTransfersInfo): string {
    const isPositionsCashTransfer = transferType === transferTypes.POSITIONS_CASH;

    if (onetimeTotalDistIt && isPositionsCashTransfer) {
      return 'Total account balance will be distributed';
    }

    if (is401kToAccount && isPositionsCashTransfer) {
      return itsInfo.totalCashAmount;
    }

    return itInfo.journalCashAmount;
  }

  generateLOAFormRequestCreateStandingAuth(
    form: StandingAuthorizationForm,
    accountInfo: State,
    checkInfo: StandingAuthCheckInfo,
    internalTransferInfo: InternalTransferInfoSA,
    wireInfo: WireInfo,
    useTestTrustDigitalToPdfFlow: boolean,
    stateCode: string
  ): LetterOfAuthorizationRequest {
    let transactionType = form.transactionType;
    let isIra = accountInfo.classifications.isIra;

    let stateTaxLabel = pdfLabelsSubmitDetails.stateTax;
    if (!accountInfo.classifications.isNraAccount && stateCode) {
      stateTaxLabel = `State Tax (${stateCode}):`
    };

    let loaRequest = new LetterOfAuthorizationRequest({
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      registrationType: accountInfo.registrationType,
      state: stateCode
    });

    if ((transactionType === 'check' && isCheckStandingAuthFormSA(form))) {
      let standingAuthDetails = form.standingAuthorizationDetails as CheckStandingAuthDetails;
      let distributionReason = standingAuthDetails.distributionReason;

      loaRequest.transactionType = standingAuthDetails.payeeType === 'thirdParty' ? Constants.checkThirdParty : Constants.checkFirstParty;

      let checkMemo: string = checkInfo.memo?.trim();
      loaRequest.toDetails = [
        new Detail(pdfLabelsToDetails.payee, checkInfo.payee),
        ...(!!checkMemo ? [new Detail(pdfLabelsToDetails.memo, [checkInfo.memo])] : []),
        new Detail(pdfLabelsToDetails.recipientAddress,
          (form.standingAuthorizationDetails as CheckStandingAuthDetails).recipient === 'firstParty' && !!checkInfo.defaultRecipient
            ? checkInfo.defaultRecipient
            : checkInfo.deliveryAddress),
      ];

      loaRequest.createSloaDetails = [
        ...(isIra ? [
          new Detail(pdfLabelsSubmitDetails.withholdFederalTax, [checkInfo.isWithholdFedTax ? 'Yes' : 'No']),
          new Detail(pdfLabelsSubmitDetails.fedTax, [checkInfo.fedTaxRate]),
          ...(checkInfo.shouldShowStateTax || accountInfo.classifications.isNraAccount ? [
            new Detail(pdfLabelsSubmitDetails.withholdStateTax, [checkInfo.isWithholdStateTax ? 'Yes' : 'No']),
            new Detail(stateTaxLabel, [checkInfo.stateTaxRate])] : [])] : []),
        ...(distributionReason && distributionReason !== '' && distributionReason !== null ? [
          new Detail(pdfLabelsSubmitDetails.distributionReason, [distributionReason])] : []),
        ...(checkInfo.checkReason && checkInfo.checkReason !== '' && checkInfo.checkReason !== null ?
          [new Detail(pdfLabelsSubmitDetails.withdrawalReason, [checkInfo.checkReason])] : [new Detail(pdfLabelsSubmitDetails.withdrawalReason, [])]),
        new Detail(pdfLabelsSubmitDetails.requestedDate, [new Date(checkInfo.requestedDate).toLocaleDateString('es-pa')]),
      ]

      loaRequest.frequency = 'One Time';
    }

    if ((transactionType === 'internal-transfer' && isInternalTransferFormSA(form))) {
      let isI401kToAccount = internalTransferInfo.toAccountInfo?.registrationType === RegTypeCode.INDIVIDUAL_401K || internalTransferInfo.toAccountInfo?.registrationType === RegTypeCode.ROTH_401K;
      if (useTestTrustDigitalToPdfFlow) {
        loaRequest.transactionType = 'Journal'
      } else {
        loaRequest.transactionType = isI401kToAccount ? Constants.journalThirdParty : internalTransferInfo.isThirdParty ? Constants.journalThirdParty : Constants.journalFirstParty;
      }

      const defaultStateTaxPercent = this.percentFormatPipe.transform(internalTransferInfo.iraFields.stateTaxPercent ?? '');
      const shouldIncludeDefaultZeroStateTax = internalTransferInfo.iraFields.stwProcessCode === 'N' && defaultStateTaxPercent === Constants.zeroPercent;

      loaRequest.toDetails = [
        new Detail(pdfLabelsToDetails.toAccount, [internalTransferInfo.toAccountInfo.accountNumber]),
        new Detail(pdfLabelsToDetails.toAccountRegistration, internalTransferInfo.toAccountInfo.accountRegistration)
      ];

      loaRequest.createSloaDetails = [
        ...(isIra ? [
          new Detail(pdfLabelsSubmitDetails.withholdFederalTax, [internalTransferInfo.iraFields.isWithholdFedTax ? 'Yes' : 'No']),
          new Detail(pdfLabelsSubmitDetails.fedTax, [internalTransferInfo.iraFields.fedTaxAmount]),
          ...(internalTransferInfo.shouldShowStateTax || shouldIncludeDefaultZeroStateTax ? [
            new Detail(pdfLabelsSubmitDetails.withholdStateTax, [internalTransferInfo.iraFields.isWithholdStateTax ? 'Yes' : 'No']),
            new Detail(stateTaxLabel, [internalTransferInfo.iraFields.stateTaxAmount || (shouldIncludeDefaultZeroStateTax ? defaultStateTaxPercent : '')])] : []),
          new Detail(pdfLabelsSubmitDetails.distributionReason, [])] : []),
        new Detail(pdfLabelsSubmitDetails.requestedDate, [this.formatDatePipe.transform(internalTransferInfo.requestedDate)]),
      ]

      loaRequest.toAccountId = this.hyphenPipe.transform(internalTransferInfo.toAccountInfo.accountNumber);
      loaRequest.frequency = 'One Time';
      if (isI401kToAccount) {
        loaRequest.isFromNoSigPdfFlow = false; //Signatures should be required for i401k account for all roles because default value isthridparty is true
      }
    }

    if (transactionType === 'wire' && isWireFormSA(form)) {
      loaRequest.transactionType = 'Wire';

      let wireStandingAuthDetails = form.standingAuthorizationDetails;
      // Find requestedDate value
      const requestedDateValue = wireInfo.requestedDate || '';

      // Build the array conditionally
      loaRequest.createSloaDetails = [
        ...(!wireInfo.isIrsAba
          ? [new Detail(pdfLabelsSubmitDetails.additionalWireInstructions, [wireStandingAuthDetails?.transmissionNotes?.toUpperCase()])]
          : []),

        // If IRS ABA, add tax fields before requestedDate
        ...(wireInfo.isIrsAba
          ? [
            new Detail(pdfLabelsSubmitDetails.transmissionNotesIrs, [wireInfo.taxRemittance?.additionalInfo]),
            new Detail(pdfLabelsSubmitDetails.taxId, [this.maskAccountNumberPipe.transform(wireInfo.taxRemittance?.taxId)]),
            new Detail(pdfLabelsSubmitDetails.taxTypeCode, [wireInfo.taxRemittance?.taxTypeCode]),
          ]
          : []),

        new Detail(pdfLabelsSubmitDetails.requestedDate, [requestedDateValue]),
        ...(isIra ? [
            new Detail(pdfLabelsSubmitDetails.withholdFederalTax, [wireInfo.withholdFedTax || accountInfo.classifications.isNraAccount ? 'Yes' : 'No']),
          new Detail(pdfLabelsSubmitDetails.fedTax, [wireStandingAuthDetails?.fedTaxPercent]),
            ...(wireInfo.stateHasTaxes || accountInfo.classifications.isNraAccount ? [
                new Detail(pdfLabelsSubmitDetails.withholdStateTax, [wireInfo.withholdStateTax ? 'Yes' : 'No']),
              new Detail(stateTaxLabel, [wireStandingAuthDetails?.stateTaxPercent])] : []),
          ] : [])
        ];

        let beneficiaryCountry = wireInfo.toDetails.beneficiaryCountry.toUpperCase();
        if (useTestTrustDigitalToPdfFlow) {
          let country = wireInfo.countryList.find(country => country.code === wireStandingAuthDetails.receivingAccountHolderCountry);
          beneficiaryCountry = country?.description?.trim()?.toUpperCase() ?? '';
        }

        this.countryListNameValue = wireInfo.countryList.map((country) => {
        return { value: country.code, name: country.description };})
         
        loaRequest.toDetails = this.getIsoToDetailsLoaRequestSA(loaRequest, wireStandingAuthDetails, this.countryListNameValue);

        loaRequest.frequency = 'On-Request';
        loaRequest.processId = wireInfo.caseKey;
        loaRequest.transferId = wireInfo.transferId;
    }

    if (useTestTrustDigitalToPdfFlow) {
      loaRequest.formsFlowPdf = true;
    }

    return loaRequest;
  }

  formatIrsTaxMonth(code: string | undefined): string {
    if (!code) {
      return '';
    }
    const description = MONTH_LIST.find(month => month.code === code)?.description ?? '';
    return (description + ' (' + code + ')').trim();
  }

  getInternalTransferInfo(internalTransferInfo: InternalTransferInfo, internalTransfersInfo: InternalTransfersInfo, toAccount: string): InternalTransferInfo {
    var verifyResponse = internalTransfersInfo.multipleToJournalsList.find(response => response.toAccountInfo.accountNumber === toAccount);
    return {
      ...internalTransferInfo,
      isSubmitAndCreate: false,
      shouldShowStateTax: internalTransfersInfo.toAccountPropertiesById[toAccount]?.shouldShowStateTax ?? false,
      totalWithdrawalAmount: verifyResponse?.totalWithdrawalAmount === Constants.totalWithdrawalResponseMessage ?
        Constants.totalWithdrawalAmountInternalTransfer : verifyResponse?.totalWithdrawalAmount ?? '',
      journalCashAmount: verifyResponse?.journalCashAmount ?? '',
      requestedDate: verifyResponse?.processDate ?? '',
      processDate: verifyResponse?.processDate ?? '',
      toAccountInfo: verifyResponse?.toAccountInfo ?? internalTransferInfo.toAccountInfo,
      isThirdParty: verifyResponse?.isThirdParty ?? false,
      isIdentical: internalTransfersInfo.toAccountPropertiesById[toAccount]?.isIdentical ?? false,
      hasWpfaRole: internalTransfersInfo.hasWpfaRole,
      iraContributionType: verifyResponse?.iraContributionType ?? internalTransferInfo.iraContributionType,
      recurringFields: {
        ...internalTransferInfo.recurringFields,
        frequencyList: []
      },
      iraFields: verifyResponse?.iraFields ?? internalTransfersInfo.iraFields[toAccount],
      positionsInfo: {
        ...internalTransferInfo.positionsInfo,
        hasSelected: true,
        list: internalTransfersInfo.rowData[toAccount]?.positionData?.filter(position => position['Shares to move']).map(position => {
          return {
            schwabSecurityId: 0,
            securityNumber: 0,
            cmtSequenceId: 0,
            valMultiplier: 0,
            symbol: position['Symbol'],
            fullName: position['Name'],
            price: 0,
            cmtRestrictionCode: '',
            isRestricted: false,
            restrictionText: '',
            cmtSelectedQuantityCode: 0,
            selectedQuantityInvalidText: '',
            selectedQuantityMinimum: 0,
            selectedQuantityMinimumDenomination: 0,
            shortName: '',
            lots: [],
            quantity: 0,
            marketValue: Number((position['Estimated market value'].replace(/[$,]/g, ""))),
            longShortCode: '',
            isSelected: false,
            selectedQuantity: Number(position['Shares to move']).toLocaleString("en-US"),
            selectedMarkteValue: 0,
            costBasis: '',
            gainLossAmount: '',
            gainLossPercent: '',
            isGain: false,
            isLoss: false,
            selectedMarketValue: Number((position['Estimated market value'].replace(/[$,]/g, ""))),
          }
        }) ?? []
      },
    };
  }

  private getFederalTaxValue(onetimeTotalDistIt: boolean, accountInfo: AccountInfo, form: ItForm): string {
    const NRA_DEFAULT_TAX_RATE = '30%';
    const COMBINE_ACCOUNTS_TYPE = 'COMBINE ACCOUNTS (0-COMB)';
    const QUALIFIED_TYPE = 'QUALIFIED (Q)';
    if (!onetimeTotalDistIt) {
      return this.getStandardFederalTax(form);
    }

    const isNraAccount = accountInfo?.classifications?.isNraAccount;
    const iraDistributionType = form?.transactionDetails?.toAccounts?.[0]?.iraDistributionType;
    const isExemptFromNraDefault =
      iraDistributionType === COMBINE_ACCOUNTS_TYPE ||
      iraDistributionType === QUALIFIED_TYPE;

    if (isNraAccount && !isExemptFromNraDefault) {
      return NRA_DEFAULT_TAX_RATE;
    }

    return this.getStandardFederalTax(form);
  }

  private getStandardFederalTax(form: ItForm): string {
    const taxGroup = form?.transactionDetails?.taxWithholdingsFormGroup;
    return taxGroup.fedTaxAmount === ''
      ? taxGroup.fedTaxAmount
      : taxGroup.fedTaxPercent;
  }
}
